import React from "react";
import { useTranslation } from "react-i18next";
import { useUserFullInfo, clearUserTokens } from "../api/users";
import { VipSection } from "./VipSection";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

interface UserViewModalProps {
    userId: number | null;
    isOpen: boolean;
    onClose: () => void;
}

export const UserViewModal: React.FC<UserViewModalProps> = ({
    userId,
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation("users");
    const { data: userInfo, isLoading, error } = useUserFullInfo(userId);
    const [isClearingTokens, setIsClearingTokens] = React.useState(false);

    if (!isOpen) return null;

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "-";
        return format(new Date(dateString), "dd.MM.yyyy HH:mm", { locale: ru });
    };

    const formatDateOnly = (dateString: string | null) => {
        if (!dateString) return "-";
        return format(new Date(dateString), "dd.MM.yyyy", { locale: ru });
    };

    const formatSex = (sex: number | null) => {
        if (sex === null) return "-";
        return sex === 1 ? "Мужской" : sex === 2 ? "Женский" : "Не указан";
    };

    const formatPack = (pack: number | null) => {
        if (pack === null) return "-";
        const packNames: Record<number, string> = {
            1: "Стандарт",
            2: "Премиум",
            3: "VIP",
        };
        return packNames[pack] || `Пакет ${pack}`;
    };

    const formatAccountType = (type: number | null) => {
        if (type === null) return "-";
        const typeNames: Record<number, string> = {
            1: "Обычный",
            2: "Премиум",
            3: "VIP",
        };
        return typeNames[type] || `Тип ${type}`;
    };

    const formatStructureStatus = (status: number | null) => {
        if (status === null) return "-";
        const statusNames: Record<number, string> = {
            0: "Не активна",
            1: "Активна",
            2: "Заблокирована",
        };
        return statusNames[status] || `Статус ${status}`;
    };

    const formatStarYearStatus = (status: number | null) => {
        if (status === null) return "-";
        const statusNames: Record<number, string> = {
            0: "Не звездный",
            1: "Звездный",
            2: "VIP звездный",
        };
        return statusNames[status] || `Статус ${status}`;
    };

    const formatSponsors = (sponsors: number[] | null) => {
        if (!sponsors || sponsors.length === 0) return "-";
        return sponsors.join(", ");
    };

    const formatSponsorsChain = (
        sponsors:
            | {
                  id: number;
                  name?: string | null;
                  surname?: string | null;
                  telegram_username?: string | null;
                  secret_cookie?: string | null;
                  member_fee_time?: string | null;
                  access_status?: number | null;
              }[]
            | null
            | undefined
    ) => {
        if (!sponsors || sponsors.length === 0) return "-";

        const total = sponsors.length;

        return (
            <>
                {sponsors.map((s, index) => {
                    const fullName =
                        [s.name, s.surname].filter(Boolean).join(" ") ||
                        s.telegram_username ||
                        `ID ${s.id}`;

                    const isHighlighted = total - index <= 10;

                    return (
                        <span
                            key={`${s.id}-${index}`}
                            className={
                                isHighlighted
                                    ? "text-blue-900 dark:text-blue-300"
                                    : undefined
                            }
                        >
                            {fullName} ({s.id}
                            {s.secret_cookie ? `, ${s.secret_cookie}` : ""}
                            )
                            {index < total - 1 && (
                                <span className="text-gray-400">
                                    {" "}
                                    →{" "}
                                </span>
                            )}
                        </span>
                    );
                })}
            </>
        );
    };

    const formatPath = (path: string[] | null) => {
        if (!path || path.length === 0) return "-";
        return path.join(" → ");
    };

    const formatAccessStatus = (status: number | null | undefined) => {
        if (status === null || status === undefined)
            return {
                label: "не указан",
                hint: "Статус аккаунта неизвестен",
                className:
                    "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
            };
        // access_status — вход в кабинет / блокировка, НЕ VIP
        const statusConfig: Record<
            number,
            { label: string; hint: string; className: string }
        > = {
            0: {
                label: "активный",
                hint: "Может входить в кабинет",
                className:
                    "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
            },
            1: {
                label: "заблокирован",
                hint: "Вход запрещён",
                className:
                    "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
            },
            2: {
                label: "ограничен",
                hint: "Ограниченный доступ",
                className:
                    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
            },
            3: {
                label: "удалён",
                hint: "Аккаунт удалён",
                className:
                    "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
            },
        };
        return statusConfig[status] || statusConfig[0];
    };

    const handleClearTokens = async () => {
        if (!userId) return;

        if (!window.confirm("Вы уверены, что хотите очистить все сессии этого пользователя?")) {
            return;
        }

        try {
            setIsClearingTokens(true);
            await clearUserTokens(userId);
            alert("Токены пользователя очищены");
        } catch (err: any) {
            console.error("Error clearing user tokens:", err);
            alert(err?.response?.data?.message || err?.message || "Ошибка при очистке токенов");
        } finally {
            setIsClearingTokens(false);
        }
    };

    const renderInfoSection = (title: string, data: Record<string, any>) => {
        return (
            <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                    {title}
                </h4>
                {Object.entries(data).map(([key, value]) => {
                    return (
                        <div key={key}>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 capitalize">
                                {key.replace(/_/g, " ")}
                            </label>
                            <div className="mt-1 text-sm text-gray-900 dark:text-white">
                                {value === null || value === undefined
                                    ? "-"
                                    : String(value)}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
                {/* Заголовок */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {userInfo?.name} {userInfo?.surname}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {/* Содержимое */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : error ? (
                        <div className="text-center py-8">
                            <div className="text-red-600 text-lg font-medium mb-2">
                                {t("view.error.title")}
                            </div>
                            <div className="text-gray-600 dark:text-gray-400">
                                {t("view.error.message")}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                                {error instanceof Error
                                    ? error.message
                                    : "Неизвестная ошибка"}
                            </div>
                        </div>
                    ) : userInfo ? (
                        <>
                            {/* Фото + VIP: колонки одной высоты */}
                            <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3 mb-6 items-stretch">
                                <aside className="flex flex-col h-full rounded-xl border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900/50 p-3.5">
                                    {userInfo.photo ? (
                                        <img
                                            src={userInfo.photo}
                                            alt={`${userInfo.name} ${userInfo.surname}`}
                                            className="w-24 h-24 self-center rounded-full object-cover border-[3px] border-blue-500 shadow-sm"
                                        />
                                    ) : (
                                        <div className="w-24 h-24 self-center rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center border-[3px] border-blue-500 shadow-sm">
                                            <span className="text-gray-400 dark:text-gray-500 text-3xl">
                                                {userInfo.name?.[0]?.toUpperCase() ||
                                                    "?"}
                                            </span>
                                        </div>
                                    )}

                                    <div
                                        className="mt-2 text-center text-sm font-medium text-gray-900 dark:text-white leading-snug line-clamp-2 px-1"
                                        title={`${userInfo.name || ""} ${userInfo.surname || ""}`.trim()}
                                    >
                                        {[userInfo.name, userInfo.surname]
                                            .filter(Boolean)
                                            .join(" ") || "—"}
                                    </div>

                                    <div className="mt-2.5 space-y-2 text-xs flex-1">
                                        <div className="rounded-lg bg-white dark:bg-gray-800/80 px-2.5 py-1.5 border border-gray-100 dark:border-gray-700">
                                            <div className="text-[10px] uppercase tracking-wide text-gray-400">
                                                ID
                                            </div>
                                            <div className="font-mono text-gray-900 dark:text-white">
                                                {userInfo.id}
                                            </div>
                                        </div>
                                        <div className="rounded-lg bg-white dark:bg-gray-800/80 px-2.5 py-1.5 border border-gray-100 dark:border-gray-700">
                                            <div className="text-[10px] uppercase tracking-wide text-gray-400">
                                                Cookie
                                            </div>
                                            <div className="font-mono text-gray-900 dark:text-white break-all">
                                                {userInfo.secret_cookie || "—"}
                                            </div>
                                        </div>
                                        <div className="rounded-lg bg-white dark:bg-gray-800/80 px-2.5 py-1.5 border border-gray-100 dark:border-gray-700">
                                            <div className="text-[10px] uppercase tracking-wide text-gray-400">
                                                Пригласил
                                            </div>
                                            <div className="text-gray-900 dark:text-white">
                                                {userInfo.inviter_id ?? "—"}
                                            </div>
                                        </div>
                                        <div className="rounded-lg bg-white dark:bg-gray-800/80 px-2.5 py-1.5 border border-gray-100 dark:border-gray-700">
                                            <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">
                                                Аккаунт
                                            </div>
                                            <span
                                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                                                    formatAccessStatus(
                                                        userInfo.access_status
                                                    ).className
                                                }`}
                                                title={
                                                    formatAccessStatus(
                                                        userInfo.access_status
                                                    ).hint
                                                }
                                            >
                                                {
                                                    formatAccessStatus(
                                                        userInfo.access_status
                                                    ).label
                                                }
                                            </span>
                                        </div>

                                        <button
                                            onClick={handleClearTokens}
                                            disabled={isClearingTokens}
                                            className="w-full h-8 bg-slate-100 hover:bg-slate-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-gray-600 px-3 rounded-lg text-xs font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                                        >
                                            {isClearingTokens && (
                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-slate-500"></div>
                                            )}
                                            {isClearingTokens
                                                ? "Очистка…"
                                                : "Очистить сессии"}
                                        </button>
                                    </div>
                                </aside>

                                <div className="flex flex-col h-full gap-3 min-w-0">
                                    <VipSection userId={userId} />

                                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900/50 p-3.5 space-y-2.5">
                                        <div className="flex gap-2 items-start">
                                            <span className="text-[10px] uppercase tracking-wide text-gray-400 w-[7.5rem] shrink-0 pt-0.5">
                                                Путь в дереве
                                            </span>
                                            <div className="text-sm text-gray-900 dark:text-white min-w-0">
                                                {formatPath(
                                                    userInfo.user_tree?.path ||
                                                        null
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex gap-2 items-start border-t border-gray-100 dark:border-gray-700 pt-2.5">
                                            <span className="text-[10px] uppercase tracking-wide text-gray-400 w-[7.5rem] shrink-0 pt-0.5">
                                                Спонсоры
                                            </span>
                                            <div className="text-sm text-gray-900 dark:text-white min-w-0">
                                                {formatSponsorsChain(
                                                    userInfo.sponsors_full
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Статусы и иконки */}
                            {userInfo.status_icons && (
                                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-gray-900 dark:to-gray-800 p-4 rounded-lg mb-6">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                        Статусы и достижения
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {userInfo.status_icons.main.length >
                                            0 && (
                                            <div>
                                                <h4 className="text-sm font-semibold mb-2">
                                                    Основные статусы:
                                                </h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {userInfo.status_icons.main.map(
                                                        (status, i) => (
                                                            <div
                                                                key={i}
                                                                className="bg-white dark:bg-gray-700 p-2 rounded flex items-center gap-2"
                                                            >
                                                                <img
                                                                    src={
                                                                        status.icon
                                                                    }
                                                                    alt={
                                                                        status.name
                                                                    }
                                                                    className="w-6 h-6"
                                                                />
                                                                <span className="text-xs">
                                                                    {
                                                                        status.name
                                                                    }
                                                                </span>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {userInfo.status_icons.patron.length >
                                            0 && (
                                            <div>
                                                <h4 className="text-sm font-semibold mb-2">
                                                    Меценаты:
                                                </h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {userInfo.status_icons.patron.map(
                                                        (status, i) => (
                                                            <div
                                                                key={i}
                                                                className="bg-white dark:bg-gray-700 p-2 rounded flex items-center gap-2"
                                                            >
                                                                <img
                                                                    src={
                                                                        status.icon
                                                                    }
                                                                    alt={
                                                                        status.name
                                                                    }
                                                                    className="w-6 h-6"
                                                                />
                                                                <span className="text-xs">
                                                                    {
                                                                        status.name
                                                                    }
                                                                </span>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {userInfo.status_icons.patron_ranks
                                            .length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-semibold mb-2">
                                                    Места в рейтинге:
                                                </h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {userInfo.status_icons.patron_ranks.map(
                                                        (status, i) => (
                                                            <div
                                                                key={i}
                                                                className="bg-white dark:bg-gray-700 p-2 rounded flex items-center gap-2"
                                                            >
                                                                <img
                                                                    src={
                                                                        status.icon
                                                                    }
                                                                    alt={
                                                                        status.name
                                                                    }
                                                                    className="w-6 h-6"
                                                                />
                                                                <span className="text-xs">
                                                                    {
                                                                        status.name
                                                                    }
                                                                </span>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {userInfo.status_icons.legacy.length >
                                            0 && (
                                            <div>
                                                <h4 className="text-sm font-semibold mb-2">
                                                    Особые статусы:
                                                </h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {userInfo.status_icons.legacy.map(
                                                        (status, i) => (
                                                            <div
                                                                key={i}
                                                                className="bg-white dark:bg-gray-700 p-2 rounded flex items-center gap-2"
                                                            >
                                                                <img
                                                                    src={
                                                                        status.icon
                                                                    }
                                                                    alt={
                                                                        status.name
                                                                    }
                                                                    className="w-6 h-6"
                                                                />
                                                                <span className="text-xs">
                                                                    {
                                                                        status.name
                                                                    }
                                                                </span>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Основная информация о пользователе */}
                            <div className="bg-blue-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                    Основная информация о пользователе
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {renderInfoSection("Идентификация", {
                                        ID: userInfo.id,
                                        Имя: userInfo.name,
                                        Фамилия: userInfo.surname,
                                        Email: userInfo.email,
                                        "Email подтвержден":
                                            userInfo.email_verified_at
                                                ? formatDate(
                                                      userInfo.email_verified_at
                                                  )
                                                : "Нет",
                                        "Secret Cookie":
                                            userInfo.secret_cookie || "-",
                                        "Ref Link": userInfo.ref_link || "-",
                                        Паспорт: userInfo.passport || "-",
                                        Ранг: userInfo.rank,
                                    })}
                                    {renderInfoSection("Контакты", {
                                        Мобильный: userInfo.mobile,
                                        "Telegram ID":
                                            userInfo.telegram_id || "-",
                                        "Telegram username":
                                            userInfo.telegram_username || "-",
                                        "Telegram Bot": userInfo.telegram_bot
                                            ? "Да"
                                            : "Нет",
                                        Instagram: userInfo.instagram || "-",
                                        Facebook: userInfo.facebook || "-",
                                    })}
                                    {renderInfoSection("Личная информация", {
                                        Страна: userInfo.country || "-",
                                        Пол: formatSex(userInfo.sex),
                                        "Дата рождения": formatDateOnly(
                                            userInfo.date_of_birth
                                        ),
                                        Город: userInfo.city || "-",
                                        Адрес: userInfo.address || "-",
                                    })}
                                    {renderInfoSection("Аккаунт", {
                                        Пакет: formatPack(userInfo.pack),
                                        "Время пакета": userInfo.pack_time
                                            ? formatDate(userInfo.pack_time)
                                            : "-",
                                        "Тип аккаунта": formatAccountType(
                                            userInfo.account_type
                                        ),
                                        Заблокирован: userInfo.is_blocked
                                            ? "Да"
                                            : "Нет",
                                        Активен: userInfo.is_active
                                            ? "Да"
                                            : "Нет",
                                        "Время членского взноса":
                                            userInfo.member_fee_time
                                                ? formatDate(
                                                      userInfo.member_fee_time
                                                  )
                                                : "-",
                                        "Дата регистрации игры":
                                            userInfo.register_date_game
                                                ? formatDate(
                                                      userInfo.register_date_game
                                                  )
                                                : "-",
                                    })}
                                    {renderInfoSection("Реферальная система", {
                                        "ID пригласившего":
                                            userInfo.inviter_id || "-",
                                        "Ref Miss": userInfo.ref_miss,
                                        "Доступные уровни": Array.isArray(
                                            userInfo.referral_level_restrictions
                                                ?.available_levels
                                        )
                                            ? userInfo.referral_level_restrictions.available_levels.join(
                                                  ", "
                                              )
                                            : "-",
                                        "Ограничения действуют": userInfo
                                            .referral_level_restrictions
                                            ?.is_restricted
                                            ? "Да"
                                            : "Нет",
                                        "Всего рефералов":
                                            userInfo.user_stats?.total_referrals ||
                                            "-",
                                        "Рефералов по уровням":
                                            userInfo.user_stats?.level_counts &&
                                            typeof userInfo.user_stats
                                                ?.level_counts === "object"
                                                ? Object.entries(
                                                      userInfo.user_stats
                                                          .level_counts as Record<
                                                          string,
                                                          number
                                                      >
                                                  )
                                                      .sort(
                                                          ([a], [b]) =>
                                                              Number(a) -
                                                              Number(b)
                                                      )
                                                      .map(
                                                          ([level, count]) =>
                                                              `${parseInt(level)+1}: ${count}`
                                                      )
                                                      .join(", ")
                                                : "-",
                                    })}
                                    {renderInfoSection("Роли и статусы", {
                                        Роли: Array.isArray(userInfo.roles)
                                            ? userInfo.roles.join(", ")
                                            : "-",
                                        Дилер: userInfo.is_dealer
                                            ? "Да"
                                            : "Нет",
                                        "Время дилера": userInfo.dealer_time
                                            ? formatDate(userInfo.dealer_time)
                                            : "-",
                                        Менеджер: userInfo.is_manager
                                            ? "Да"
                                            : "Нет",
                                        "Время менеджера": userInfo.manager_time
                                            ? formatDate(userInfo.manager_time)
                                            : "-",
                                        "Деактивация менеджера":
                                            userInfo.manager_deact_time
                                                ? formatDate(
                                                      userInfo.manager_deact_time
                                                  )
                                                : "-",
                                    })}
                                    {renderInfoSection("Системные настройки", {
                                        Язык: userInfo.language || "-",
                                        "Автоплатеж (карта Stripe)":
                                            userInfo.autopay ? "Да" : "Нет",
                                        "Автосписание structural (тогл)":
                                            userInfo.structural_autopay ===
                                            undefined
                                                ? "—"
                                                : userInfo.structural_autopay
                                                  ? "Вкл"
                                                  : "Выкл",
                                        "Stripe Customer ID":
                                            userInfo.stripe_customer_id || "-",
                                        "Показывать графики":
                                            userInfo.show_graphs ? "Да" : "Нет",
                                        "Последний вход": userInfo.last_login
                                            ? formatDate(userInfo.last_login)
                                            : "-",
                                        "Последний шанс использован":
                                            userInfo.last_chance_used
                                                ? "Да"
                                                : "Нет",
                                        "Комментарии к последнему шансу":
                                            userInfo.last_chance_comments ||
                                            "-",
                                    })}
                                    {renderInfoSection("Даты", {
                                        Создан: formatDate(userInfo.created_at),
                                        Обновлен: formatDate(
                                            userInfo.updated_at
                                        ),
                                    })}
                                </div>
                            </div>

                            {/* Банковская информация */}
                            <div className="bg-red-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                    Банковская информация
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {renderInfoSection("Банковские реквизиты", {
                                        "Банковский счёт":
                                            userInfo.bank_account || "-",
                                        "SWIFT код": userInfo.bank_swift || "-",
                                        "Банковская карта":
                                            userInfo.bank_card || "-",
                                    })}
                                    {renderInfoSection("Криптокошельки", {
                                        "BEP20 адрес": userInfo.bep20 || "-",
                                        "TRC20 адрес": userInfo.trc20 || "-",
                                    })}
                                </div>
                            </div>

                            {/* Программы */}
                            {userInfo.programs &&
                                Array.isArray(userInfo.programs) &&
                                userInfo.programs.length > 0 && (
                                    <div className="bg-teal-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                            Участие в программах
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {renderInfoSection(
                                                "Активные программы",
                                                {
                                                    Программы:
                                                        userInfo.programs.join(
                                                            ", "
                                                        ),
                                                    "Статус структуры":
                                                        formatStructureStatus(
                                                            userInfo.structure_status
                                                        ),
                                                    "Статус благотворительности":
                                                        userInfo.charity_status !==
                                                        null
                                                            ? userInfo.charity_status
                                                            : "-",
                                                    "Статус недвижимости":
                                                        userInfo.estate_status !==
                                                        null
                                                            ? userInfo.estate_status
                                                            : "-",
                                                    "Статус звездного года":
                                                        formatStarYearStatus(
                                                            userInfo.star_year_status
                                                        ),
                                                    "Дата вступления в Star":
                                                        userInfo.star_join_date
                                                            ? formatDate(
                                                                  new Date(
                                                                      userInfo.star_join_date *
                                                                          1000
                                                                  ).toISOString()
                                                              )
                                                            : "-",
                                                }
                                            )}
                                        </div>
                                    </div>
                                )}

                            {/* Гарант система */}
                            <div className="bg-cyan-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                    Гарант система
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {renderInfoSection("Гарант переводы", {
                                        "Гарант исходящие": userInfo.garant_out,
                                        "Гарант входящие": userInfo.garant_in,
                                        Положительные: userInfo.garant_positive,
                                        Отрицательные: userInfo.garant_negative,
                                    })}
                                </div>
                            </div>

                            {/* Патроны */}
                            <div className="bg-amber-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                    Меценатство
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {renderInfoSection("Серебряный меценат", {
                                        "Сумма (этап 1)":
                                            userInfo.patronages?.silver_stage_1
                                                ?.amount ?? "-",
                                        "Сумма (этап 2)":
                                            userInfo.patronages?.silver_stage_2
                                                ?.amount ?? "-",
                                        "Время (этап 1)": userInfo.patronages
                                            ?.silver_stage_1?.next_payment_at
                                            ? formatDate(
                                                  userInfo.patronages
                                                      .silver_stage_1
                                                      .next_payment_at
                                              )
                                            : "-",
                                        "Время (этап 2)": userInfo.patronages
                                            ?.silver_stage_2?.next_payment_at
                                            ? formatDate(
                                                  userInfo.patronages
                                                      .silver_stage_2
                                                      .next_payment_at
                                              )
                                            : "-",
                                        "Включен (этап 2)": userInfo.patronages
                                            ?.settings?.silver_enabled
                                            ? "Да"
                                            : "Нет",
                                        "Коэффициент (этап 2)":
                                            userInfo.patronages?.settings
                                                ?.silver_coefficient ?? "-",
                                    })}
                                    {renderInfoSection("Золотой меценат", {
                                        "Сумма (этап 1)":
                                            userInfo.patronages?.gold_stage_1
                                                ?.amount ?? "-",
                                        "Сумма (этап 2)":
                                            userInfo.patronages?.gold_stage_2
                                                ?.amount ?? "-",
                                        "Время (этап 1)": userInfo.patronages
                                            ?.gold_stage_1?.next_payment_at
                                            ? formatDate(
                                                  userInfo.patronages
                                                      .gold_stage_1
                                                      .next_payment_at
                                              )
                                            : "-",
                                        "Время (этап 2)": userInfo.patronages
                                            ?.gold_stage_2?.next_payment_at
                                            ? formatDate(
                                                  userInfo.patronages
                                                      .gold_stage_2
                                                      .next_payment_at
                                              )
                                            : "-",
                                        "Включен (этап 2)": userInfo.patronages
                                            ?.settings?.gold_enabled
                                            ? "Да"
                                            : "Нет",
                                        "Коэффициент (этап 2)":
                                            userInfo.patronages?.settings
                                                ?.gold_coefficient ?? "-",
                                        Иммунитет: userInfo.gold_immunity,
                                        Пропуски: userInfo.gold_misses,
                                        Потеряно: userInfo.gold_lost,
                                    })}
                                </div>
                            </div>

                            {/* Инвестиции */}
                            <div className="bg-lime-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                    Инвестиции
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {renderInfoSection(
                                        "Инвестиционные возможности",
                                        {
                                            "Может инвестировать":
                                                userInfo.can_invest
                                                    ? "Да"
                                                    : "Нет",
                                            "URL авторизации":
                                                userInfo.invest_auth_url || "-",
                                            "Гарант инвестиций":
                                                userInfo.invest_garant,
                                        }
                                    )}
                                </div>
                            </div>

                            {/* Пригласитель */}
                            {userInfo.inviter && (
                                <div className="bg-violet-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                        Пригласитель
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {renderInfoSection(
                                            "Информация о пригласителе",
                                            {
                                                ID: userInfo.inviter.id,
                                                Имя: `${userInfo.inviter.name} ${userInfo.inviter.surname}`,
                                                Email: userInfo.inviter.email,
                                                Мобильный:
                                                    userInfo.inviter.mobile,
                                                "Secret Cookie":
                                                    userInfo.inviter
                                                        .secret_cookie,
                                                Ранг: userInfo.inviter.rank,
                                                "Последний вход": userInfo
                                                    .inviter.last_login
                                                    ? formatDate(
                                                          userInfo.inviter
                                                              .last_login
                                                      )
                                                    : "-",
                                                Программы: Array.isArray(
                                                    userInfo.inviter.programs
                                                )
                                                    ? userInfo.inviter.programs.join(
                                                          ", "
                                                      )
                                                    : "-",
                                            }
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Наследник */}
                            {userInfo.heir && (
                                <div className="bg-rose-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                        Наследник
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {renderInfoSection(
                                            "Информация о наследнике",
                                            {
                                                "Полное имя":
                                                    userInfo.heir.full_name,
                                                Имя: userInfo.heir.heir_name,
                                                Фамилия:
                                                    userInfo.heir.heir_surname,
                                                Паспорт:
                                                    userInfo.heir.heir_passport,
                                                "Дата рождения": formatDateOnly(
                                                    userInfo.heir.heir_dob
                                                ),
                                            }
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Реферальное дерево */}
                            {userInfo.user_tree && (
                                <div className="bg-green-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                        Информация о реферальном дереве
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {renderInfoSection("Дерево", {
                                            "ID пользователя":
                                                userInfo.user_tree.user_id,
                                            Путь: formatPath(
                                                userInfo.user_tree.path
                                            ),
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Кошелек */}
                            {userInfo.wallet && (
                                <div className="bg-yellow-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                        Кошелек
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {renderInfoSection(
                                            "Основные кошельки",
                                            {
                                                "Основной кошелек":
                                                    userInfo.wallet.main_wallet,
                                                "USDT кошелек":
                                                    userInfo.wallet.usdt_wallet,
                                            }
                                        )}
                                        {renderInfoSection("Будущие кошельки", {
                                            "Основной будущий":
                                                userInfo.wallet
                                                    .main_future_wallet,
                                            "USDT будущий":
                                                userInfo.wallet
                                                    .usdt_future_wallet,
                                            "Основной звездный":
                                                userInfo.wallet
                                                    .main_future_star_wallet,
                                            "USDT звездный":
                                                userInfo.wallet
                                                    .usdt_future_star_wallet,
                                        })}
                                        {renderInfoSection("Прибыль и траты", {
                                            "Месячная прибыль (осн)":
                                                userInfo.wallet
                                                    .main_monthly_profit,
                                            "Месячная прибыль (USDT)":
                                                userInfo.wallet
                                                    .usdt_monthly_profit,
                                            "Всего потрачено (осн)":
                                                userInfo.wallet.total_spent,
                                            "Всего потрачено (USDT)":
                                                userInfo.wallet
                                                    .usdt_total_spent,
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Статистика */}
                            {userInfo.stats && (
                                <div className="bg-purple-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                        Статистика за месяц
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {renderInfoSection("Рефералы", {
                                            Клиенты:
                                                userInfo.stats.clients_month,
                                            Дилеры: userInfo.stats
                                                .dillers_month,
                                            Менеджеры:
                                                userInfo.stats.managers_month,
                                            "Всего рефералов":
                                                userInfo.stats
                                                    .total_referrals_month,
                                        })}
                                        {renderInfoSection("Компании", {
                                            Компании:
                                                userInfo.stats.companies_month,
                                            "Заработано компаний":
                                                userInfo.stats
                                                    .companies_earned_month,
                                            "Заработано акций":
                                                userInfo.stats
                                                    .shares_earned_month,
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Звездный пользователь */}
                            {userInfo.alny_user_star && (
                                <div className="bg-pink-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                        Звездный пользователь
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {renderInfoSection("Информация", {
                                            "ID пользователя":
                                                userInfo.alny_user_star.user_id,
                                            "Дата оплаты": formatDate(
                                                userInfo.alny_user_star
                                                    .user_pay_date
                                            ),
                                            Спонсоры: formatSponsors(
                                                userInfo.alny_user_star.sponsors
                                            ),
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Карты Stripe */}
                            {userInfo.alny_cards_stripe &&
                                userInfo.alny_cards_stripe.length > 0 && (
                                    <div className="bg-indigo-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                            Карты Stripe
                                        </h3>
                                        <div className="space-y-3">
                                            {userInfo.alny_cards_stripe.map(
                                                (card, index) => (
                                                    <div
                                                        key={card.id}
                                                        className="bg-white dark:bg-gray-800 p-3 rounded border"
                                                    >
                                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                            {renderInfoSection(
                                                                `Карта ${
                                                                    index + 1
                                                                }`,
                                                                {
                                                                    "ID карты":
                                                                        card.card_id,
                                                                    "Маска карты":
                                                                        card.card_mask,
                                                                    "Тип карты":
                                                                        card.card_type,
                                                                    Количество:
                                                                        card.count,
                                                                    Дата: formatDate(
                                                                        card.date_time
                                                                    ),
                                                                }
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                )}
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <div className="text-gray-600 dark:text-gray-400">
                                Нет данных для отображения
                            </div>
                        </div>
                    )}
                </div>

                {/* Кнопки */}
                <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                        {t("close")}
                    </button>
                </div>
            </div>
        </div>
    );
};
